import type { Dict } from '../i18n/ru'
import type { Dialog, DialogSummary, Message, Participant } from './types'

const NAME_KEY = 'linia_name'

/** Имя, под которым человек подписывается в диалогах. Живёт на устройстве до появления аккаунта. */
export const myName = () => localStorage.getItem(NAME_KEY) ?? ''
export const setMyName = (n: string) => localStorage.setItem(NAME_KEY, n.trim())

type Titled = {
  kind: 'duo' | 'group'
  sharedTitle: string | null
  participants: Participant[]
  personalTitle?: string | null
  me?: { personalTitle: string | null } | null
}

/** Правило имени: личное → общее → «Я и {другой}» → по умолчанию. */
export function dialogTitle(d: Titled, t: Dict): string {
  const personal = d.personalTitle ?? d.me?.personalTitle ?? null
  if (personal) return personal
  if (d.sharedTitle) return d.sharedTitle
  const others = d.participants.filter((p) => !p.me && p.name)
  if (d.kind === 'duo' && others[0]) return t.dialogs.titleWith(others[0].name)
  if (d.kind === 'group' && others.length) return others.map((p) => p.name).slice(0, 3).join(', ')
  return d.kind === 'group' ? t.dialogs.titleGroup : t.dialogs.titleDefault
}

export function nameOf(from: string, d: { participants: Participant[] }, t: Dict): string {
  if (from === 'ai') return t.dialogs.ai
  if (from === 'system') return ''
  const p = d.participants.find((x) => x.pid === from)
  if (!p) return t.dialogs.friend
  return p.me ? t.dialogs.you : p.name || t.dialogs.friend
}

export const isMine = (m: Message, d: Dialog) => !!d.me && m.from === d.me.pid

/** Системные сообщения приходят как «код:имя[:значение]», текст рисует клиент по своему словарю. */
export function sysText(m: Message, t: Dict): string {
  const [code, name = '', ...rest] = m.text.split(':')
  const value = rest.join(':')
  switch (code) {
    case 'joined': return t.dialogs.sys.joined(name)
    case 'renamed': return t.dialogs.sys.renamed(name, value)
    case 'locked': return t.dialogs.sys.locked(name)
    case 'unlocked': return t.dialogs.sys.unlocked(name)
    default: return m.text
  }
}

export function messageUrl(dialogId: string, msgId?: number | null) {
  return `${location.origin}/d/${dialogId}${msgId ? `/m/${msgId}` : ''}`
}

/** Текст шера по типу хода: вопрос, ответ на чужой вопрос, просто рисунок. */
export function shareTextFor(m: Message, d: Dialog, t: Dict): string {
  const url = messageUrl(d.id, m.id)
  const who = d.me?.name ?? ''
  if (m.kind === 'drawing' && m.question) return t.dialogs.shareQuestion(who, m.question, url)
  if (m.kind === 'guess') return t.dialogs.shareAnswered(url)
  return t.dialogs.shareDrawing(url)
}

/** Web Share, если есть; иначе — WhatsApp по прямой ссылке. */
export async function shareMove(text: string, url: string): Promise<'shared' | 'whatsapp' | 'copied'> {
  if (navigator.share) {
    try {
      await navigator.share({ text, url })
      return 'shared'
    } catch { /* отменил — падаем ниже */ }
  }
  const wa = `https://wa.me/?text=${encodeURIComponent(text)}`
  const w = window.open(wa, '_blank', 'noopener')
  if (w) return 'whatsapp'
  try { await navigator.clipboard.writeText(text) } catch { /* ничего */ }
  return 'copied'
}

export function lastPreview(s: DialogSummary, t: Dict): string {
  const m = s.last
  if (!m) return ''
  const who = nameOf(m.from, s, t)
  if (m.kind === 'drawing') return `${who}: ${m.question ? m.question : '✏️'}`
  if (m.kind === 'guess') return `${who}: ${m.text}`
  if (m.kind === 'ai') return `${t.dialogs.ai}: ${m.text}`
  return `${who}: ${m.text}`
}
